/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React from 'react';
import PropTypes from 'prop-types';
import * as ChartJS from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { Box } from '@mui/material';
import { shortDayLabel } from './mavenUtils';
import {
  AIADMIN_SECONDARY_TEXT_COLOR,
  AIADMIN_TEXT_PRIMARY,
  AIADMIN_TRENDS_COUNT_CHART_COLORS
} from '../../common/styles/aiadminThemeTokens';

ChartJS.Chart.register(
  ChartJS.CategoryScale,
  ChartJS.LinearScale,
  ChartJS.BarElement,
  ChartJS.LineElement,
  ChartJS.PointElement,
  ChartJS.Title,
  ChartJS.Tooltip,
  ChartJS.Legend
);

// Same teal palette and text colours as the dashboard charts (Trends).
const SERIES_COLORS = AIADMIN_TRENDS_COUNT_CHART_COLORS;
const CHART_TEXT = AIADMIN_TEXT_PRIMARY;
const CHART_TICKS = AIADMIN_SECONDARY_TEXT_COLOR;

/** A line or bar chart from the answer's chart block. */
const AnswerChart = ({ chart }) => {
  const data = {
    labels: chart.x.map(shortDayLabel),
    datasets: chart.series.map((s, i) => ({
      label: s.name,
      data: s.data,
      borderColor: SERIES_COLORS[i % SERIES_COLORS.length],
      backgroundColor: SERIES_COLORS[i % SERIES_COLORS.length],
      tension: 0.2,
      pointRadius: 2
    }))
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: Boolean(chart.title), text: chart.title, color: CHART_TEXT },
      legend: { labels: { color: CHART_TEXT } }
    },
    scales: {
      x: { ticks: { color: CHART_TICKS, maxRotation: 0, autoSkip: true } },
      y: { ticks: { color: CHART_TICKS }, beginAtZero: true }
    }
  };
  const ChartType = chart.type === 'bar' ? Bar : Line;
  return (
    <Box sx={{ height: 220, my: 1.5 }}>
      <ChartType data={data} options={options} />
    </Box>
  );
};

AnswerChart.propTypes = {
  chart: PropTypes.shape({
    type: PropTypes.string,
    title: PropTypes.string,
    x: PropTypes.array,
    series: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, data: PropTypes.array }))
  }).isRequired
};

export default AnswerChart;
